import { useState } from 'react';
import { BellRing, Building2, ClipboardList, Gauge, PhoneIncoming, RefreshCw, Settings2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { useAction, useQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { OverviewTab } from './OverviewTab';
import { CallbacksTab } from './CallbacksTab';
import { NewCallersTab } from './NewCallersTab';
import { ContactsTab } from './ContactsTab';
import { CompaniesTab } from './CompaniesTab';
import { InsightsTab } from './InsightsTab';
import { SettingsTab } from './SettingsTab';
import { errorMessage } from './format';

type Tab = 'overview' | 'callbacks' | 'callers' | 'contacts' | 'companies' | 'insights' | 'settings';
type Counts = { openCallbacks: number; overdueCallbacks: number; openFollowUps: number; newCallers: number; insights: number };
type ImportStatus = { status: string; phase?: string; lastSuccessfulImportAt?: string; lastError?: string };

export function CrmLayout() {
  const { data, hasFeature } = useCrm();
  const [tab, setTab] = useState<Tab>('overview');
  const [taskOwner, setTaskOwner] = useState<string | null>(null);
  const counts = useQuery<Counts>(backendApi.crm.getCounts, data ? {} : 'skip');
  const importStatus = useQuery<ImportStatus>(backendApi.cms.getImportStatus, data ? {} : 'skip');
  const startImport = useAction(backendApi.cms.startImportNow);
  const [syncing, setSyncing] = useState(false);

  if (data === undefined) {
    return (
      <Card>
        <p className="text-gray-500">Loading CRM…</p>
      </Card>
    );
  }
  if (data === null) {
    return (
      <Card>
        <p className="text-gray-700 dark:text-gray-300">The CRM is not available for this account.</p>
      </Card>
    );
  }

  const sync = async () => {
    setSyncing(true);
    try {
      await startImport({});
      toast.success('Phone book and call import started');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSyncing(false);
    }
  };

  const openWork = (counts?.openCallbacks ?? 0) + (counts?.openFollowUps ?? 0);
  const tabs: Array<{ id: Tab; label: string; icon: typeof Users; badge?: number; alert?: boolean; show: boolean }> = [
    { id: 'overview', label: 'Overview', icon: Gauge, show: true },
    { id: 'callbacks', label: hasFeature('missedCallRecovery') ? 'Callbacks' : 'Tasks', icon: ClipboardList, badge: openWork, alert: Boolean(counts?.overdueCallbacks), show: true },
    { id: 'callers', label: 'New callers', icon: PhoneIncoming, badge: counts?.newCallers, show: hasFeature('newCallerInbox') },
    { id: 'contacts', label: 'Contacts', icon: Users, show: true },
    { id: 'companies', label: 'Companies', icon: Building2, show: true },
    { id: 'insights', label: 'Insights', icon: BellRing, badge: counts?.insights, show: hasFeature('insights') },
    { id: 'settings', label: 'Settings', icon: Settings2, show: true },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-4xl">CRM</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {importStatus?.status === 'running'
              ? `Importing${importStatus.phase ? `: ${importStatus.phase}` : ''}…`
              : importStatus?.lastSuccessfulImportAt
                ? `Phone book last imported ${new Date(importStatus.lastSuccessfulImportAt).toLocaleString()}${hasFeature('missedCallRecovery') || hasFeature('screenPop') ? '; calls arrive within minutes' : '; calls daily'}`
                : 'Not imported yet'}
          </p>
          {importStatus?.status === 'error' && importStatus.lastError && <p className="text-sm text-red-600 dark:text-red-400 mt-1">Last import failed: {importStatus.lastError}</p>}
        </div>
        {data.canManage && (
          <button
            type="button"
            onClick={() => void sync()}
            disabled={syncing || importStatus?.status === 'running'}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${syncing || importStatus?.status === 'running' ? 'animate-spin' : ''}`} />
            Import now
          </button>
        )}
      </div>

      <nav className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700" aria-label="CRM sections">
        {tabs
          .filter((item) => item.show)
          .map((item) => {
            const Icon = item.icon;
            const active = tab === item.id;
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => { if (item.id === 'callbacks') setTaskOwner(null); setTab(item.id); }}
                className={`flex items-center gap-2 px-3 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  active ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
                {Boolean(item.badge) && (
                  <span
                    className={`px-1.5 py-0.5 text-xs rounded-full ${
                      item.alert ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {item.badge! > 999 ? '999+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
      </nav>

      {tab === 'overview' && <OverviewTab onOpenTab={(next, owner) => { setTaskOwner(owner ?? null); setTab(next); }} />}
      {tab === 'callbacks' && <CallbacksTab key={taskOwner === null ? 'all' : `owner:${taskOwner}`} ownerFilter={taskOwner} />}
      {tab === 'callers' && <NewCallersTab />}
      {tab === 'contacts' && <ContactsTab />}
      {tab === 'companies' && <CompaniesTab />}
      {tab === 'insights' && <InsightsTab />}
      {tab === 'settings' && <SettingsTab />}
    </div>
  );
}
